import { Button, ButtonsGroup, Modal, ModalProps, notify, Text, useModal } from '@billy/design-system'
import { ReactElement, useCallback } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { BankMatchUiFlow, UiSource } from '@features/reconciliation/enums'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { ModalId } from '../../../../../../enums/modalId'
import { NotificationKeys } from '../../../../../../enums/notificationKeys'
import { useSegment } from '../../../../../../hooks'
import { useAcceptAllMatchSuggestions } from '../../contexts/acceptAllMatchSuggestionsContext'
import { useCreateMultipleReconciliations } from '../../hooks/useCreateMultipleReconciliations'

type AcceptAllMatchSuggestionsConfirmationModalProps = ModalProps

export const AcceptAllMatchSuggestionsConfirmationModal = ({
  ...modalProps
}: AcceptAllMatchSuggestionsConfirmationModalProps): ReactElement => {
  const { t } = useTranslation()
  const { organization } = useUserOrganization()
  const { totalMatchSuggestions, matchSuggestions, removeSuccessfullyMatched } = useAcceptAllMatchSuggestions()
  const { close: closeModal } = useModal(ModalId.AcceptAllMatchSuggestionsModal)
  const { track } = useSegment()

  const { createMultipleReconciliations, isProcessing } = useCreateMultipleReconciliations({
    uiSource: UiSource.OldReconView,
    uiFlow: BankMatchUiFlow.Matched,
    onSettled: (succeededMatches, failedMatches) => {
      if (succeededMatches.length > 0) {
        notify({
          id: NotificationKeys.AcceptAllMatchSuggestions,
          variant: 'success',
          message: t('bank_integration.accept_matches_modal.success', { count: succeededMatches.length }),
        })
        removeSuccessfullyMatched(succeededMatches)
      }

      if (failedMatches.length > 0) {
        notify({
          id: NotificationKeys.AcceptAllMatchSuggestionsError,
          variant: 'error',
          message: t('bank_integration.accept_matches_modal.error', { count: failedMatches.length }),
        })
      }

      closeModal()
    },
    onError: () => {
      notify({
        id: NotificationKeys.AcceptAllMatchSuggestionsError,
        variant: 'error',
        message: t('something_went_wrong'),
      })
      closeModal()
    },
  })

  const organizationId = organization?.id

  const handleAcceptClick = useCallback(() => {
    if (!organizationId) {
      return
    }

    createMultipleReconciliations({ matchSuggestions, organizationId })
    track('XXX - Reconciliation Suggestions Accepted (Client)')
  }, [organizationId, createMultipleReconciliations, matchSuggestions, track])

  return (
    <Modal size="s" {...modalProps} forceRender closable={!isProcessing}>
      <Modal.Header>
        <Text variant="h2">{t('bank_integration.accept_matches_modal.title', { totalMatchSuggestions })}</Text>
      </Modal.Header>
      <Modal.Body>
        <Text>
          <Trans i18nKey="bank_integration.accept_matches_modal.body" />
        </Text>
      </Modal.Body>
      <Modal.Footer>
        <ButtonsGroup>
          <Button onClick={closeModal} variant="filled" disabled={isProcessing}>
            {t('cancel')}
          </Button>
          <Button onClick={handleAcceptClick} variant="filled" loading={isProcessing}>
            {t('bank_integration.accept_matches_modal.accept')}
          </Button>
        </ButtonsGroup>
      </Modal.Footer>
    </Modal>
  )
}
